// # Filename: src/components/poker/shared/ChipTransferOverlay.jsx
// The poker-chip visual family: a static bet-amount chip (BetMarker), the chip that
// flies from the pot to a winner's stack (TravelingChip), and the overlay that lays
// out a batch of those flights (ChipTransferOverlay). Kept together since they all
// render the same chip look and should stay visually in sync with each other.

import React from "react";
import { motion } from "framer-motion";
import Tooltip from "../../ui/Tooltip";

const CHIP_TRANSFER_CHIP_DURATION_SECONDS = 1.15;
const CHIP_TRANSFER_STAGGER_SECONDS = 0.2;
const CHIP_TRANSFER_STREAM_CHIPS = 5;
const CHIP_TRANSFER_START_POINT = { xPct: 50, yPct: 56 };

export function BetMarker({ amount, name, className = "", style = undefined }) {
  if (!amount) return null;
  return (
    <Tooltip content={`${name || "Player"} bet ${amount}`}>
      <div
        className={[
          "inline-flex flex-col items-center gap-0.5 text-[clamp(8px,0.9cqw,12px)] font-black text-cyan-50 shadow-[0_10px_24px_rgba(0,0,0,0.34)]",
          className,
        ].join(" ")}
        style={style}
      >
        <span className="grid h-[clamp(19px,2.1cqw,27px)] w-[clamp(19px,2.1cqw,27px)] place-items-center rounded-full border border-cyan-100/55 bg-[repeating-conic-gradient(from_8deg,#67e8f9_0deg_14deg,#0e7490_14deg_28deg)] p-[clamp(1px,0.16cqw,2px)] shadow-[inset_0_0_0_1px_rgba(8,47,73,0.42),inset_0_4px_10px_rgba(255,255,255,0.2)]">
          <span className="grid h-full w-full place-items-center rounded-full border border-cyan-950/45 bg-[radial-gradient(circle_at_35%_30%,#ecfeff_0_11%,#67e8f9_12%_45%,#0891b2_46%_100%)] text-[clamp(7px,0.78cqw,10px)] font-black leading-none text-slate-950">
            {amount}
          </span>
        </span>
      </div>
    </Tooltip>
  );
}

function TravelingChip({ stream, chipIndex }) {
  const start = CHIP_TRANSFER_START_POINT;
  const end = stream.end;

  return (
    <motion.div
      className="absolute z-50 grid h-[clamp(15px,1.55cqw,20px)] w-[clamp(15px,1.55cqw,20px)] place-items-center rounded-full border border-cyan-100/55 bg-[repeating-conic-gradient(from_8deg,#67e8f9_0deg_14deg,#0e7490_14deg_28deg)] p-[clamp(1px,0.16cqw,2px)] shadow-[0_10px_24px_rgba(0,0,0,0.34),inset_0_0_0_1px_rgba(8,47,73,0.42),inset_0_4px_10px_rgba(255,255,255,0.2)]"
      style={{ left: `${start.xPct}%`, top: `${start.yPct}%`, translateX: "-50%", translateY: "-50%" }}
      initial={{ opacity: 0, scale: 0.72, rotate: -18 }}
      animate={{
        left: [`${start.xPct}%`, `${end.xPct}%`, `${end.xPct}%`],
        top: [`${start.yPct}%`, `${end.yPct}%`, `${end.yPct}%`],
        opacity: [0, 1, 1, 0],
        scale: [0.72, 0.94, 1.08],
        rotate: [-18, 0, 0],
      }}
      transition={{
        duration: CHIP_TRANSFER_CHIP_DURATION_SECONDS,
        delay: chipIndex * CHIP_TRANSFER_STAGGER_SECONDS,
        ease: [0.22, 0.78, 0.26, 1],
        times: [0, 0.82, 1],
      }}
    >
      <span className="grid h-full w-full place-items-center rounded-full border border-cyan-950/45 bg-[radial-gradient(circle_at_35%_30%,#ecfeff_0_11%,#67e8f9_12%_45%,#0891b2_46%_100%)] text-[clamp(5px,0.55cqw,7px)] font-black leading-none text-slate-950">
        {stream.amount}
      </span>
    </motion.div>
  );
}

export default function ChipTransferOverlay({ streams }) {
  if (!streams.length) return null;
  return (
    <div className="pointer-events-none absolute inset-0 z-50">
      {streams.flatMap((stream) => (
        Array.from({ length: CHIP_TRANSFER_STREAM_CHIPS }).map((_, chipIndex) => (
          <TravelingChip
            key={`${stream.key}-${chipIndex}`}
            stream={stream}
            chipIndex={chipIndex}
          />
        ))
      ))}
    </div>
  );
}
